import { atom, useAtom, useAtomValue, useSetAtom } from "jotai";
import { find, flatten, groupBy, keys, map, prop, propEq, reject } from "ramda";
import { type FC, Fragment, type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Route, Routes, useNavigate, useParams } from "react-router";
import { useLocation } from "react-router-dom";
import { Item, useAsyncList } from "react-stately";
import { titleCase } from "title-case";
import {
  ActionButtonIntent,
  Card,
  CardGrid,
  CardGridArea,
  CardSection,
  HeaderBar,
  HeaderDivider,
  LoadingSpinner,
  MenuOptionItem,
  type PanelApi,
  PanelGroup,
  PanelOrientation,
  PanelPosition,
  SelectList,
  Spacer,
  SymbolIcon,
  Tag,
  TagIntent,
  toTimeAgo,
  TriggerButton,
  TriggerButtonIntent,
  VStack,
} from "widgets";
import { useURLSearchParam } from "widgets/hooks";

import { useError } from "@/app-utils/graphql/useError.ts";
import { getProjectPageTips } from "@/app-utils/help.tsx";
import { useDocumentTitle } from "@/app-utils/hooks/useDocumentTitle.ts";
import { useEventNotificationGridUpdate } from "@/app-utils/hooks/useNotificationUpdate.ts";
import { pluralizeWord } from "@/app-utils/string.js";
import type { InputTableQuerySpec, Project, User } from "@/graphql.ts";
import { ToggleFiltersButton } from "@/modules/core/components/ToggleFiltersButton.tsx";
import { FileBrowser } from "@/modules/files/components/FileBrowser.tsx";
import { useFileBrowserStore } from "@/modules/files/stores/file-browser.ts";
import { ProjectColoredBadge } from "@/modules/projects/components/ProjectColoredWidgets.tsx";
import { ProjectTagMultiSelect } from "@/modules/projects/components/ProjectTagMultiSelect.tsx";
import { TagManagementCard } from "@/modules/projects/components/TagManagementCard.js";
import {
  type GetProjectForPagesQuery,
  useGetProjectForPagesQuery,
  useGetProjectStatisticsQuery,
} from "@/modules/projects/graphql/projects-queries.gql.ts";
import { isLastAdmin } from "@/modules/projects/utils/isLastAdmin.ts";
import { SavedItemsView } from "@/modules/saved-items/components/SavedItemsView.tsx";
import {
  type GetSavedItemsListingQuery,
  useGetSavedItemsListingLazyQuery,
} from "@/modules/saved-items/graphql/saved-items.gql.ts";
import { savedItemHeaderInfoAtom } from "@/modules/saved-items/store/atoms.ts";
import { getUserDisplayName } from "@/modules/users/utils/user.ts";
import style from "@/modules/workflow/styles/ToolBar.module.css";
import { getProjectLabel } from "@/redux/modules/config.ts";
import { markAsRead } from "@/redux/modules/notifications.tsx";
import { deleteProject, loadProject, removeProjectMember, updateProjectRole } from "@/redux/modules/project.ts";
import { createUniqueKeyFromTableSpec } from "@/redux/modules/table-data.ts";
import type { IAppState } from "@/redux/reducer.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";
import { DATATABLE_ID_TYPE, ENTITY_TYPE, EVENT_TABLE_COLUMNS } from "@/settings/settings.ts";
import type { GetArrayElementType } from "@/types.js";
import ColumnSelectionButton from "@/ui-modules/core/ColumnSelectionButton.tsx";
import { ContentPlaceholder } from "@/ui-modules/core/ContentPlaceholder.tsx";
import DataTableContainer from "@/ui-modules/core/grid/DataTableContainer.tsx";
import { ModalActionWidget, ModalControlledOverlay } from "@/ui-modules/core/overlays/components/Overlays.tsx";
import { ConfirmationDialog } from "@/ui-modules/core/overlays/Dialogs.tsx";
import TipTour from "@/ui-modules/core/TipTour.tsx";
import { CapabilitiesCard } from "@/ui-modules/entities/cards/CapabilitiesCard.js";
import MembersCard from "@/ui-modules/entities/cards/MembersCard.js";
import { MetadataCard } from "@/ui-modules/entities/cards/MetadataCard.js";
import { ProjectGroupCard } from "@/ui-modules/entities/cards/ProjectGroupCard.js";
import AddMemberPopover from "@/ui-modules/projects/AddMemberPopover.js";
import { EditProjectDialog } from "@/ui-modules/projects/EditProjectDialog.js";
import { ProjectActivityPanel } from "@/ui-modules/projects/ProjectActivityPanel.tsx";

import styleLayout from "../../../ui-modules/core/styles/PageLayout.module.scss";

const subtitleAtom = atom(" ");
const actionsAtom = atom<ReactNode[]>([]);
const projectActivityPanelOpenStateAtom = atom<boolean>(false);

export function ProjectPage() {
  const dispatch = useDispatch<ReduxDispatch>();
  const setActivityPanelOpen = useSetAtom(projectActivityPanelOpenStateAtom);
  const location = useLocation();

  const isPrivateWorkspace = useSelector<IAppState, boolean>((state) => state.project.project?.isPrivateWorkspace);
  const { projectId: id } = useParams<"projectId">();
  const projectId = id ? Number(id) : undefined;

  const notificationIdSP = useURLSearchParam("notificationid");
  const notificationId = notificationIdSP ? Number.parseInt(notificationIdSP, 10) : undefined;
  const activityId = useURLSearchParam("activityid");

  useEffect(() => {
    // force opening the activity panel if some specific activity needs to be targeted (only at mounting time)
    if (activityId) {
      setActivityPanelOpen(true);
    }
    // if we got a notificationId via route query param, mark the notification as read
    if (notificationId) {
      void dispatch(markAsRead(notificationId, false));
    }
  }, []);

  return (
    <TipTour tips={getProjectPageTips(isPrivateWorkspace)}>
      <VStack spacing={0} style={{ gridArea: "page", overflow: "hidden" }}>
        <PanelGroup
          key="saved-items"
          orientation={PanelOrientation.Horizontal}
          isContinuous
          defaultSize={280}
          minSize={240}
          maxSize={500}
          fixedPanel={PanelPosition.Leading}
        >
          <Routes>
            <Route index element={<Navigate to={{ pathname: "files", search: location.search }} replace />} />
            <Route path="savedItems/:type/:subType" element={<ProjectNavigationPanel projectId={projectId} />} />
            <Route path="savedItems" element={<Navigate to="savedItems/view/view" replace />} />
            <Route path=":cat" element={<ProjectNavigationPanel projectId={projectId} />} />
          </Routes>
          <WithActivityPanel projectId={projectId} />
        </PanelGroup>
      </VStack>
    </TipTour>
  );
}

const tagIdsAtom = atom<number[]>([]);

type Listing = GetArrayElementType<GetSavedItemsListingQuery["savedItems"]["listing"]>;
export type Item = Listing & { id: string };

export interface List {
  name: string;
  items: Item[];
}

const ProjectNavigationPanel: FC<{ projectId?: number }> = ({ projectId = null }) => {
  const navigate = useNavigate();
  const { type, subType, cat } = useParams<"type" | "subType" | "cat">();

  const projectStats = useGetProjectStatisticsQuery({ variables: { projectId }, skip: projectId == null });
  const eventCount = projectStats.data?.project?.projectStatistics.eventCount;
  void useError(projectStats.error);

  const [tagIds, setTagIds] = useAtom(tagIdsAtom);
  const setSavedItemHeaderInfo = useSetAtom(savedItemHeaderInfoAtom);

  const [fetch, { error, loading }] = useGetSavedItemsListingLazyQuery();
  void useError(error);

  const list = useAsyncList<List>({
    async load() {
      try {
        const { data } = await fetch({ variables: { projectId, tagIds: tagIds.length > 0 ? tagIds : null } });
        const listing = reject(propEq(0, "count"), data.savedItems.listing);
        const grouped = groupBy((item) => (item.type === "parent" ? "parent" : "other"), listing);
        const items = map(
          (key) => ({
            name: key,
            items: map((item) => ({ id: `${item.type}_${item.subType}`, ...item }), grouped[key]!),
          }),
          keys(grouped),
        );

        return { items };
      } catch {
        return { items: [] };
      }
    },
  });

  const getItem = useCallback(
    (key: string) => {
      const flatList = flatten(map(prop("items"), list.items));
      return find(propEq(key, "id"), flatList);
    },
    [list.items],
  );

  useEffect(() => {
    if (cat) {
      list.setSelectedKeys(new Set([]));
    } else if (type && subType) {
      list.setSelectedKeys(new Set([`${type}_${subType}`]));
    }
  }, [type, subType, cat]);

  useEffect(() => {
    list.reload();
  }, [tagIds]);

  useEffect(() => {
    if (loading) {
      setSavedItemHeaderInfo({ title: null, count: null });
    } else {
      const item = getItem([...list.selectedKeys][0]);
      if (item) {
        setSavedItemHeaderInfo({
          title: item.name,
          count: item.count === item.filteredCount ? item.count : item.filteredCount,
        });
      }
    }
  }, [list.selectedKeys, getItem, loading]);

  const sampleParentItems = find<List>(propEq("parent", "name"), list.items)?.items ?? [];
  const otherSavedItems = find<List>(propEq("other", "name"), list.items)?.items ?? [];

  useEffect(
    () => () => {
      // reset
      setTagIds([]);
      setSavedItemHeaderInfo({ title: null, count: null });
    },
    [],
  );

  const [areFiltersVisible, setFiltersVisible] = useState<boolean>(false);

  return (
    <Card
      noBorder
      fillHeight
      scrollable
      style={{ backgroundColor: "var(--panel-background)" }}
      className={style.header}
      title="Project"
      actions={[
        <Spacer key="spacer" />,
        <TriggerButton key="workflow-refetch-widget" icon="sync" label="Refresh" onPress={() => list.reload()} />,
      ]}
    >
      <CardSection>
        <SelectList
          noBorder
          style={{ margin: "0 -5px" }}
          selectedKeys={cat ? [cat] : []}
          onSelectionChange={(keys) => {
            const key = [...keys][0];
            navigate(`../${key}`);
          }}
          selectionMode="single"
          disallowEmptySelection
        >
          <Item key="files">
            <MenuOptionItem title="Files" useHover />
          </Item>
          <Item key="events">
            <MenuOptionItem title="Playbook Events" tag={eventCount ?? undefined} useHover />
          </Item>
          <Item key="settings">
            <MenuOptionItem title="Settings" useHover tipId="project-settings-menu" />
          </Item>
        </SelectList>
      </CardSection>

      <CardSection
        title="Views, Groups, Flags, Lists"
        actions={[
          <ToggleFiltersButton
            key="toggle-filters"
            areFiltersVisible={areFiltersVisible}
            toggleFiltersVisibility={() => {
              if (tagIds?.length) {
                setTagIds([]);
              }
              setFiltersVisible((toggle) => !toggle);
            }}
          />,
        ]}
      >
        {areFiltersVisible && (
          <ProjectTagMultiSelect
            label="Filter by tag"
            projectId={projectId ?? undefined}
            isRequired
            onSelectionChange={setTagIds}
            selectedIds={tagIds}
          />
        )}
        {list.isLoading ? (
          <LoadingSpinner />
        ) : (
          <SelectList
            noBorder
            style={{ margin: "0 -5px" }}
            items={otherSavedItems}
            selectedKeys={list.selectedKeys}
            onSelectionChange={(keys) => {
              if (keys instanceof Set) {
                const item = getItem([...keys][0]);
                if (item) {
                  list.setSelectedKeys(keys);
                  navigate(`../savedItems/${item.type}/${encodeURIComponent(item.subType)}`);
                }
              }
            }}
            selectionMode="single"
            disallowEmptySelection
          >
            {(item) => (
              <Item key={item.id}>
                <MenuOptionItem
                  title={titleCase(pluralizeWord(item.name))}
                  tag={
                    item.count === item.filteredCount ? (
                      item.count
                    ) : (
                      <Tag
                        label={item.filteredCount}
                        intentColor={item.filteredCount === 0 ? TagIntent.Secondary : TagIntent.Primary}
                      />
                    )
                  }
                  useHover
                />
              </Item>
            )}
          </SelectList>
        )}
      </CardSection>
      {!loading && otherSavedItems.length === 0 && (
        <ContentPlaceholder mini>
          When you are working, look for the option to save pages, flags, genomes, etc. so that you can access, organize
          or analyze them from here in the future.
        </ContentPlaceholder>
      )}
      {sampleParentItems.length > 0 && (
        <CardSection title="Sample Parents">
          {list.isLoading ? (
            <LoadingSpinner />
          ) : (
            <SelectList
              noBorder
              style={{ margin: "0 -5px" }}
              items={find<List>(propEq("parent", "name"), list.items)?.items ?? []}
              selectedKeys={list.selectedKeys}
              onSelectionChange={(keys) => {
                if (keys instanceof Set) {
                  const item = getItem([...keys][0]);
                  if (item) {
                    list.setSelectedKeys(keys);
                    navigate(`../savedItems/${item.type}/${encodeURIComponent(item.subType)}`);
                  }
                }
              }}
              selectionMode="single"
              disallowEmptySelection
            >
              {(item) => (
                <Item key={item.id}>
                  <MenuOptionItem title={titleCase(pluralizeWord(item.name))} tag={item.count} useHover />
                </Item>
              )}
            </SelectList>
          )}
        </CardSection>
      )}
    </Card>
  );
};

export type ProjectForPage = GetProjectForPagesQuery["project"];

const WithActivityPanel: FC<{ projectId?: number }> = ({ projectId = null }) => {
  const subtitle = useAtomValue(subtitleAtom);
  const actions = useAtomValue(actionsAtom);
  const activityPanelOpen = useAtomValue(projectActivityPanelOpenStateAtom);
  const setActivityPanelOpen = useSetAtom(projectActivityPanelOpenStateAtom);

  const { data, error, refetch } = useGetProjectForPagesQuery({ variables: { projectId }, skip: projectId == null });
  const project = data?.project;
  void useError(error);

  const apiRef = useRef<PanelApi>(null);
  const [mount, setMount] = useState(false);

  useEffect(() => {
    if (activityPanelOpen) {
      setMount(true);
      setTimeout(() => {
        apiRef.current?.open();
      }, 0);
    } else {
      setTimeout(() => {
        apiRef.current?.close();
      }, 0);
    }
  }, [activityPanelOpen]);

  const panelButton = (
    <Fragment key="project-activity-panel">
      <HeaderDivider />
      <TriggerButton
        icon={
          <SymbolIcon icon={activityPanelOpen ? "right_panel_close" : "right_panel_open"} filled={activityPanelOpen} />
        }
        label={`${activityPanelOpen ? "Close" : "Show"} Activity`}
        onPress={() => setActivityPanelOpen((open) => !open)}
      />
    </Fragment>
  );

  return (
    projectId &&
    project && (
      <PanelGroup
        className={styleLayout.page}
        style={{ padding: 0, backgroundColor: "var(--light-background)" }}
        apiRef={apiRef}
        orientation={PanelOrientation.Horizontal}
        defaultSize={450}
        minSize={300}
        maxSize={550}
        fixedPanel={PanelPosition.Trailing}
        onMoveEnd={(_s, _m, isClosed) => {
          // In case the Panel is opened with the Handler
          setActivityPanelOpen(!isClosed);
        }}
      >
        <div style={{ display: "flex", width: "100%", height: "100%", flexDirection: "column", gap: 0 }}>
          <HeaderBar
            title={project.name}
            subtitle={subtitle}
            icon={<ProjectColoredBadge project={project} noLink />}
            style={{ boxShadow: "inset 0 -1px var(--ui-border)" }}
            infoAttributes={
              project
                ? [
                    {
                      label: "User Group",
                      description: project.isPrivateWorkspace ? undefined : project.userGroup?.name,
                    },
                    { label: "Description", description: project.description },
                    {
                      label: "Last Activity",
                      description: project.lastModified ? toTimeAgo(project.lastModified) : undefined,
                    },
                  ]
                : undefined
            }
            actions={[...actions, panelButton]}
          />
          <Routes>
            <Route path="savedItems/:type/:subType" element={<ProjectSavedItemsPage project={project} />} />
            <Route path="files" element={<ProjectFilesPage project={project} />} />
            <Route path="events" element={<ProjectPageEventsPage project={project} />} />
            <Route path="settings" element={<ProjectPageSettingsPage projectId={projectId} refetch={refetch} />} />
          </Routes>
        </div>
        {/* projectId because loaded from redux in this case */}
        {mount && <ProjectActivityPanel projectId={project.id} />}
      </PanelGroup>
    )
  );
};

const ProjectSavedItemsPage: FC<{ project: ProjectForPage }> = ({ project }) => {
  const { type, subType } = useParams<"type" | "subType">();
  const tagIds = useAtomValue(tagIdsAtom);
  const setSubtitle = useSetAtom(subtitleAtom);

  useEffect(() => {
    setSubtitle("Saved Items");

    return () => {
      setSubtitle(" ");
    };
  }, []);

  return (
    project &&
    type &&
    subType && <SavedItemsView type={type} subType={subType} projectId={project.id} tagIds={tagIds} />
  );
};

const ProjectFilesPage: FC<{ project: ProjectForPage }> = ({ project }) => {
  const setSubtitle = useSetAtom(subtitleAtom);
  useDocumentTitle(titleCase(`File Browser${project ? ` - ${project.name}` : ""}`));

  useEffect(() => {
    setSubtitle("File Browser");

    return () => {
      setSubtitle(" ");
    };
  }, []);

  return project && <FileBrowser useStore={useFileBrowserStore} initialFolderId={project.parentFileId!} />;
};

const ProjectPageEventsPage: FC<{ project: ProjectForPage }> = ({ project }) => {
  const setSubtitle = useSetAtom(subtitleAtom);
  const setActions = useSetAtom(actionsAtom);

  const eventsTableSpec: InputTableQuerySpec = project
    ? {
        id: project?.id,
        idType: DATATABLE_ID_TYPE.ENTITY,
        queryType: ENTITY_TYPE.PROJECT,
        resultType: ENTITY_TYPE.EVENT,
      }
    : undefined;

  const eventsTableInitOptions = {
    sortExpression: `-${EVENT_TABLE_COLUMNS.CREATED}`,
  };

  useDocumentTitle(titleCase(`Playbook Events${project ? ` - ${project.name}` : ""}`));

  useEffect(() => {
    setSubtitle("Playbook Events");
    setActions([
      <ColumnSelectionButton
        portalContext={createUniqueKeyFromTableSpec(eventsTableSpec)}
        key="events-page-column-selector"
      />,
    ]);

    return () => {
      setSubtitle(" ");
      setActions([]);
    };
  }, []);

  useEventNotificationGridUpdate(eventsTableSpec);

  return (
    project &&
    eventsTableSpec && (
      <DataTableContainer
        key={createUniqueKeyFromTableSpec(eventsTableSpec)}
        tableSpec={eventsTableSpec}
        hideCheckboxes={true}
        tableInitializationOptions={eventsTableInitOptions}
        showButtonLabel={false}
      />
    )
  );
};

const ProjectPageSettingsPage: FC<{ projectId?: number; refetch?: () => Promise<unknown> }> = ({
  projectId = null,
  refetch,
}) => {
  const setSubtitle = useSetAtom(subtitleAtom);
  const setActions = useSetAtom(actionsAtom);

  const dispatch = useDispatch<ReduxDispatch>();
  const user = useSelector<IAppState, User>((state) => state.auth.session);
  const project = useSelector<IAppState, Project>((state) => state.project.project);
  const isLoading = useSelector<IAppState, boolean>((state) => state.project.isLoading);

  useDocumentTitle(titleCase(`Settings${project ? ` - ${project.name}` : ""}`));

  useEffect(() => {
    setSubtitle("Settings");
    setActions([
      <TriggerButton
        icon="exit_to_app"
        onPress={() => removeUserDialogHandler(user)} // this is you
        key="project-page-settings-page-action-1"
        isDisabled={leaveProjectDisabledTooltip ?? undefined}
        tooltip="Leave this project. Caution, you will not have access to the related project resources anymore."
        label="Leave"
        intent={TriggerButtonIntent.Danger}
      />,
      <HeaderDivider key="project-page-settings-page-action-5" />,
      <ModalActionWidget
        key="project-page-settings-page-action-3"
        actionWidget={({ open }) => (
          <TriggerButton
            icon="person_add"
            isDisabled={addUsersDisabledTooltip ?? undefined}
            tooltip="Add more users to this project."
            onPress={() => open()}
            label="Add Members"
          />
        )}
      >
        {({ close }) => <AddMemberPopover onHide={() => close()} project={project} />}
      </ModalActionWidget>,
      <ModalActionWidget
        key="project-page-settings-page-action-4"
        actionWidget={({ open }) => (
          <TriggerButton
            icon="settings"
            onPress={() => open()}
            isDisabled={configureProjectDisabledTooltip() ?? undefined}
            tooltip={configureProjectDisabledTooltip() ?? "Modify this project"}
            label="Settings"
          />
        )}
      >
        {({ close }) => (
          <EditProjectDialog
            onApply={() => {
              close();
              void refetch?.();
            }}
            onCancel={close}
            project={project}
          />
        )}
      </ModalActionWidget>,
      <ModalActionWidget
        key="project-page-settings-page-action-6"
        actionWidget={({ open }) => (
          <TriggerButton
            icon="delete"
            intent={TriggerButtonIntent.Danger}
            isDisabled={configureProjectDisabledTooltip() ?? undefined}
            tooltip={configureProjectDisabledTooltip() ?? "Delete this project"}
            onPress={() => open()}
            label="Delete"
          />
        )}
      >
        {({ close }) => (
          <ConfirmationDialog
            headerProps={{ title: `Delete ${project?.name ?? getProjectLabel(true, false)}?` }}
            mainActionButtonProps={{
              label: `Delete ${getProjectLabel(true, false)}`,
              intent: ActionButtonIntent.Danger,
              onPress: () => dispatch(deleteProject(project)).then(close),
            }}
            closeFn={close}
          >
            All the data related to {getProjectLabel(false, false)} <strong>"{project?.name}"</strong> will be deleted
            (e.g. saved lists, views, flags, ...) and cannot be recovered.
          </ConfirmationDialog>
        )}
      </ModalActionWidget>,
    ]);

    return () => {
      setSubtitle(" ");
      setActions([]);
    };
  }, [project]);

  useEffect(() => {
    // Need to load it because lots of components still rely to it.
    void dispatch(loadProject(projectId));
  }, [projectId]);

  // permissions
  const addUsersDisabledTooltip =
    !!project &&
    (project.isUserAdmin
      ? project.isPublic
        ? "This project is public"
        : project.isPrivateWorkspace
          ? "You cannot add users to a private workspace"
          : null
      : "You must be administrator to add members to this project");
  const leaveProjectDisabledTooltip =
    !!project &&
    (isLastAdmin(project)
      ? "You cannot leave this project because you are the last administrator."
      : project.isPublic
        ? "You cannot leave this project because it is a Public project."
        : null);
  const configureProjectDisabledTooltip = () => {
    if (!project?.isUserAdmin) {
      return "You must be administrator to configure or delete this project";
    } else if (project?.isPrivateWorkspace) {
      // this tooltip can be displayed only if the url is manipulated to access
      // the settings section of a private workspace, otherwise
      // the settings page is not accessible via the navpanel
      return "The private workspace cannot be deleted";
    }
    return null;
  };

  // user removal
  const [userToRemove, setUserToRemove] = useState<User | null>(null);
  const [showUserRemovalDialog, setShowUserRemovalDialog] = useState(false);
  const removeUserDialogHandler = useCallback((userToRemove: User) => {
    setUserToRemove(userToRemove);
    setShowUserRemovalDialog(true);
  }, []);

  return (
    <>
      {isLoading && <LoadingSpinner inCenter={true} />}
      {project && (
        <>
          <CardGrid>
            <MembersCard
              project={project}
              ownUser={user}
              updateProjectRole={(input) => dispatch(updateProjectRole(input))}
              removeUserHandler={removeUserDialogHandler}
              area={CardGridArea.Header}
            />
            <ProjectGroupCard project={project} area={CardGridArea.Panel} />
            <MetadataCard project={project} user={user} area={CardGridArea.Panel} />
            <CapabilitiesCard project={project} area={CardGridArea.Panel} />
            <TagManagementCard area={CardGridArea.Panel} projectId={project.id} />
          </CardGrid>
          {/* --- Dialogs --- */}
          {showUserRemovalDialog && (
            <ModalControlledOverlay
              isOpen
              title="Remove User"
              close={() => {
                setUserToRemove(null);
                setShowUserRemovalDialog(false);
              }}
              id="project-delete-confirmation-dialog"
            >
              {({ close }) => {
                const self = userToRemove?.id === user?.id;
                const userLabel = self ? "yourself" : getUserDisplayName(userToRemove ?? user);

                return (
                  <ConfirmationDialog
                    headerProps={{ title: `Remove ${userLabel}?` }}
                    mainActionButtonProps={{
                      label: `Remove`,
                      intent: ActionButtonIntent.Danger,
                      onPress: () =>
                        dispatch(removeProjectMember({ projectId: project?.id, userId: userToRemove?.id })).then(close),
                    }}
                    closeFn={close}
                  >
                    <div>
                      Are you sure you want to remove {userLabel} from <strong>"{project?.name}"</strong>?
                    </div>
                    {self && <div>You will not be able to access this {getProjectLabel(false, false)} anymore.</div>}
                  </ConfirmationDialog>
                );
              }}
            </ModalControlledOverlay>
          )}
        </>
      )}
    </>
  );
};
